import { Fragment, useEffect, useMemo, useState } from "react";
import { Modal } from "react-bootstrap";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit, faLocationDot } from "@fortawesome/free-solid-svg-icons";
import { TableLoader } from "../../../Common/ShimmerLoader";
import { tableRowBg } from "../../../Common/helper/helpar";
import ComplaintDetailTable from "./ComplaintDetailTable";
import { formatComplaintDateTime, useComplaintStore } from "./ComplaintStore/useComplaintStore";

const FIGMA = {
  navy: "var(--color-primary)",
  orange: "#F5A623",
  textMuted: "#6B7280",
  expandBg: "#F1F5F9",
  subHeaderBg: "rgba(var(--color-primary-rgb), 0.04)",
  subHeaderText: "var(--color-primary)",
  border: "#eef0f3",
};

// Same look as the shared tables (theme.css): light tinted header with
// primary-colour text, white rows separated by a thin divider, no grid lines.
const HEAD_BG =
  "linear-gradient(rgba(var(--color-primary-rgb), 0.07), rgba(var(--color-primary-rgb), 0.07)), #ffffff";

const PAGE_SIZE_OPTIONS = [5, 10, 25];

const MAIN_COLUMNS = [
  "S.No",
  "Image",
  "CG No",
  "Complainant Name",
  "Complainant Contact Number",
  "Complaint Type",
  "Complaint Category",
  "Description",
  "Zone",
  "Ward",
  "Street Location",
  "View On Map",
  "Complaint Created On",
  "Complaint Closure On",
  "Mode Of Reporting",
  "Predefined SLA Duration (Min)",
  "Actual Duration (Hr:Min)",
  "Status",
  "Close Time Location",
  "Closing Image",
  "Action Remarks",
  "Revised Ward",
  "Revised Street Location",
  "Closed By",
  "Actions",
];

const thStyle = {
  position: "sticky",
  top: 0,
  zIndex: 10,
  whiteSpace: "nowrap",
  border: "none",
  background: HEAD_BG,
  color: "var(--color-primary)",
  padding: "12px 14px",
  fontSize: "12.5px",
  fontWeight: 600,
  fontFamily: '"Poppins", sans-serif',
  textAlign: "center",
};

const subThStyle = {
  whiteSpace: "nowrap",
  border: "none",
  borderBottom: `1px solid ${FIGMA.border}`,
  padding: "10px 14px",
  fontSize: "13px",
  fontWeight: 600,
  textAlign: "center",
  backgroundColor: FIGMA.subHeaderBg,
  color: FIGMA.subHeaderText,
};

const tdStyle = {
  whiteSpace: "nowrap",
  border: "none",
  borderBottom: `1px solid ${FIGMA.border}`,
  padding: "10px 14px",
  textAlign: "center",
  color: "#4b5565",
  fontSize: "13px",
  backgroundColor: "#FFFFFF",
};

const subTdStyle = {
  border: "none",
  borderBottom: `1px solid ${FIGMA.border}`,
  padding: "10px 14px",
  fontSize: "13px",
  textAlign: "center",
  color: "#4b5565",
  backgroundColor: "#FFFFFF",
};

const ExpandButton = ({ isExpanded, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={isExpanded ? "Collapse row" : "Expand row"}
    style={{
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      width: 18,
      height: 18,
      borderRadius: 3,
      border: `1px solid ${isExpanded ? FIGMA.orange : "#D1D5DB"}`,
      backgroundColor: "#FFFFFF",
      color: isExpanded ? FIGMA.orange : "#6B7280",
      fontSize: "12px",
      fontWeight: 500,
      cursor: "pointer",
      lineHeight: 1,
      padding: 0,
    }}
  >
    {isExpanded ? "−" : "+"}
  </button>
);

const ComplaintImage = ({ src, fallback, alt, onPreview }) => {
  const imageSrc = src || fallback;

  if (!imageSrc) return "-";

  return (
    <img
      src={imageSrc}
      alt={alt}
      onClick={() => onPreview?.(imageSrc)}
      onError={(event) => {
        if (fallback && event.currentTarget.src !== fallback) {
          event.currentTarget.src = fallback;
        }
      }}
      style={{
        width: 42,
        height: 42,
        objectFit: "cover",
        borderRadius: 4,
        margin: "0 auto",
        display: "block",
        cursor: "pointer",
      }}
    />
  );
};

const ComplaintExpandableTable = ({
  rows = [],
  complaintImage,
  statusBadgeStyles,
  isProd,
  onEdit,
  pagination,
  onPageChange,
  onPageSizeChange,
  loading = false,
}) => {
  const {
    assignmentDetailsByComplaintId,
    isAssignmentLoading,
    loadingAssignmentComplaintId,
    fetchComplaintAssignedDetails,
  } = useComplaintStore();
  const [previewImage, setPreviewImage] = useState("");
  const [expandedIds, setExpandedIds] = useState([]);
  const [localPage, setLocalPage] = useState(1);
  const [localRowsPerPage, setLocalRowsPerPage] = useState(10);

  const isServerPagination = Boolean(pagination);
  const currentPage = isServerPagination
    ? pagination.currentPage
    : localPage;
  const rowsPerPage = isServerPagination
    ? pagination.pageSize
    : localRowsPerPage;
  const totalResults = isServerPagination
    ? pagination.totalCount
    : rows.length;
  const totalPages = Math.max(1, Math.ceil(totalResults / rowsPerPage));

  const paginatedRows = useMemo(() => {
    if (isServerPagination) return rows;

    const start = (currentPage - 1) * rowsPerPage;
    return rows.slice(start, start + rowsPerPage);
  }, [rows, currentPage, rowsPerPage, isServerPagination]);

  const pageNumbers = useMemo(() => {
    const pages = [];
    const maxVisible = 6;
    for (let i = 1; i <= Math.min(totalPages, maxVisible); i += 1) {
      pages.push(i);
    }
    return pages;
  }, [totalPages]);

  const rangeStart = totalResults ? (currentPage - 1) * rowsPerPage + 1 : 0;
  const rangeEnd = Math.min(currentPage * rowsPerPage, totalResults);

  useEffect(() => {
    setExpandedIds([]);
    if (!isServerPagination) {
      setLocalPage(1);
    }
  }, [rows, isServerPagination]);

  useEffect(() => {
    if (!isServerPagination && localPage > totalPages) {
      setLocalPage(totalPages);
    }
  }, [localPage, totalPages, isServerPagination]);

  const handlePageChange = (page) => {
    if (isServerPagination) {
      onPageChange?.(page);
      return;
    }
    setLocalPage(page);
  };

  const handlePageSizeChange = (nextPageSize) => {
    if (isServerPagination) {
      onPageSizeChange?.(nextPageSize);
      return;
    }
    setLocalRowsPerPage(nextPageSize);
    setLocalPage(1);
  };

  const getRowId = (row) => row.complaintId ?? row.cgNo;

  const toggleExpand = async (row) => {
    const rowId = getRowId(row);
    const isExpanded = expandedIds.includes(rowId);

    if (isExpanded) {
      setExpandedIds((prev) => prev.filter((id) => id !== rowId));
      return;
    }

    setExpandedIds((prev) => [...prev, rowId]);

    const complaintId = row.complaintId?.toString();
    if (
      complaintId &&
      !assignmentDetailsByComplaintId[complaintId]
    ) {
      await fetchComplaintAssignedDetails(row.complaintId);
    }
  };

  const renderMainRowCells = (row, serialNo, rowBg) => {





    const cellStyle = { ...tdStyle, backgroundColor: rowBg };

    return (
      <>
        <td style={cellStyle}>{serialNo}</td>
        <td style={cellStyle}>
          <ComplaintImage
            src={row.complaintImage}
            alt="Complaint"
            onPreview={setPreviewImage}
          />
        </td>
        <td style={cellStyle}>{row.cgNo || "N/A"}</td>
        <td style={cellStyle}>{row.complainantName || "N/A"}</td>
        <td style={cellStyle}>{row.contactNo || "N/A"}</td>
        <td style={cellStyle}>{row.complaintType || "N/A"}</td>
        <td style={cellStyle}>{row.complaintCategory || "N/A"}</td>
        <td style={cellStyle}>{row.description || "N/A"}</td>
        <td style={cellStyle}>{row.zoneName || "N/A"}</td>
        <td style={cellStyle}>{row.wardNo || "N/A"}</td>
        <td style={cellStyle}>{row.colonyName || "N/A"}</td>
        <td style={cellStyle}>
          {row.latitude && row.longitude ? (
            <a
              href={`https://www.google.com/maps?q=${row.latitude},${row.longitude}`}
              target="_blank"
              rel="noreferrer"
              style={{ color: "#dc3545", textDecoration: "none" }}
            >
              <FontAwesomeIcon icon={faLocationDot} className="me-1" /> View
            </a>
          ) : (
            "-"
          )}
        </td>
        <td style={cellStyle}>{formatComplaintDateTime(row.complaintCreatedOn)}</td>
        <td style={cellStyle}>{formatComplaintDateTime(row.complaintClosureOn)}</td>
        <td style={cellStyle}>{row.modeOfReporting || "-"}</td>
        <td style={cellStyle}>{row.predefinedSlaDurationMin ?? "-"}</td>
        <td style={cellStyle}>{row.actualDuration || "-"}</td>
        <td style={cellStyle}>
          <span
            style={{
              ...(statusBadgeStyles[row.status] || statusBadgeStyles.Open),
              padding: "4px 10px",
              borderRadius: 4,
              display: "inline-block",
              minWidth: 86,
            }}
          >
            {row.status}
          </span>
        </td>
        <td style={cellStyle}>
          {row.closeTimeLatitude && row.closeTimeLongitude ? (
            <a
              href={`https://www.google.com/maps?q=${row.closeTimeLatitude},${row.closeTimeLongitude}`}
              target="_blank"
              rel="noreferrer"
              style={{ color: "#dc3545", textDecoration: "none" }}
            >
              <FontAwesomeIcon icon={faLocationDot} className="me-1" /> View
            </a>
          ) : (
            "N/A"
          )}
        </td>
        <td style={cellStyle}>
          {row.closeImage ? (
            <ComplaintImage
              src={row.closeImage}
              alt="Closing"
              onPreview={setPreviewImage}
            />
          ) : (
            "-"
          )}
        </td>
        <td style={cellStyle}>{row.actionRemarks || "N/A"}</td>
        <td style={cellStyle}>{row.revisedWardNo || "N/A"}</td>
        <td style={cellStyle}>{row.revisedStreetLocation || "N/A"}</td>
        <td style={cellStyle}>{row.modifiedBy || "N/A"}</td>
        <td style={cellStyle}>
          <button
            type="button"
            className={isProd ? "edit-ghmc" : "edit-swm"}
            onClick={() => onEdit(row)}
            disabled={row.status === "Closed" || row.status === "Invalid"}
            aria-label={`Edit ${row.cgNo}`}
            style={
              row.status === "Closed" || row.status === "Invalid"
                ? { opacity: 0.45, cursor: "not-allowed", pointerEvents: "none" }
                : undefined
            }
            title={
              row.status === "Closed" || row.status === "Invalid"
                ? "Edit is disabled for closed or invalid complaints"
                : "Edit complaint"
            }
          >
            <FontAwesomeIcon icon={faEdit} />
          </button>
        </td>
      </>
    );
  };

  return (
    <div>
      {loading ? (
        <TableLoader height={500} columns={8} />
      ) : (
        <>
          <h6
            style={{
              margin: "0 0 12px",
              fontSize: "16px",
              fontWeight: 600,
              color: "#231F20",
            }}
          >
            Complaint List
          </h6>

          <div
            style={{
              overflow: "hidden",
              borderRadius: 16,
              border: `1px solid ${FIGMA.border}`,
              backgroundColor: "#FFFFFF",
              padding: "8px 10px 4px",
              boxShadow:
                "0 1px 2px rgba(17, 24, 39, 0.03), 0 6px 20px rgba(17, 24, 39, 0.05)",
            }}
          >
            <div style={{ maxHeight: 430, overflow: "auto" }}>
              <table
                style={{
                  minWidth: 2600,
                  width: "100%",
                  borderCollapse: "separate",
                  borderSpacing: 0,
                  textAlign: "center",
                  fontSize: "14px",
                }}
              >
                <thead>
                  <tr>
                    <th
                      style={{
                        ...thStyle,
                        width: 48,
                      }}
                    />
                    {MAIN_COLUMNS.map((col) => (
                      <th key={col} style={thStyle}>
                        {col}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {!paginatedRows.length ? (
                    <tr>
                      <td
                        colSpan={MAIN_COLUMNS.length + 1}
                        style={{ ...tdStyle, padding: "24px" }}
                      >
                        No data available
                      </td>
                    </tr>
                  ) : (
                    paginatedRows.map((row, rowIndex) => {
                      const rowId = getRowId(row);
                      const serialNo = (currentPage - 1) * rowsPerPage + rowIndex + 1;
                      const isExpanded = expandedIds.includes(rowId);
                      const rowBg = tableRowBg(rowIndex);
                      const assignmentHistory =
                        assignmentDetailsByComplaintId[row.complaintId?.toString()] ??
                        [];

                      return (
                        <Fragment key={rowId}>
                          <tr style={{ backgroundColor: rowBg }}>
                            <td style={{ ...tdStyle, backgroundColor: rowBg }}>
                              <ExpandButton
                                isExpanded={isExpanded}
                                onClick={() => toggleExpand(row)}
                              />
                            </td>
                            {renderMainRowCells(row, serialNo, rowBg)}
                          </tr>

                          {isExpanded && (
                            <tr style={{ backgroundColor: FIGMA.expandBg }}>
                              <td
                                colSpan={MAIN_COLUMNS.length + 1}
                                style={{
                                  border: `1px solid ${FIGMA.border}`,
                                  padding: "16px 20px",
                                }}
                              >
                                <ComplaintDetailTable
                                  assignmentHistory={assignmentHistory}
                                  isLoading={
                                    isAssignmentLoading &&
                                    loadingAssignmentComplaintId ===
                                    row.complaintId?.toString()
                                  }
                                  statusBadgeStyles={statusBadgeStyles}
                                  subThStyle={subThStyle}
                                  subTdStyle={subTdStyle}
                                />
                              </td>
                            </tr>
                          )}
                        </Fragment>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div
            style={{
              marginTop: 16,
              paddingTop: 12,
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 16,
            }}
          >
            <p style={{ margin: 0, fontSize: "14px", color: FIGMA.textMuted }}>
              Showing {rangeStart}-{rangeEnd} of {totalResults} results
            </p>

            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                gap: 20,
              }}
            >
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  fontSize: "14px",
                  color: FIGMA.textMuted,
                  margin: 0,
                }}
              >
                Row per page
                <select
                  value={rowsPerPage}
                  onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                  style={{
                    appearance: "none",
                    borderRadius: 6,
                    border: `1px solid ${FIGMA.border}`,
                    backgroundColor: "#F0F4F8",
                    padding: "6px 28px 6px 12px",
                    fontSize: "14px",
                    color: "#374151",
                    outline: "none",
                  }}
                >
                  {PAGE_SIZE_OPTIONS.map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
              </label>

              <nav
                style={{ display: "flex", alignItems: "center", gap: 6 }}
                aria-label="Pagination"
              >
                <button
                  type="button"
                  onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 32,
                    height: 32,
                    border: "none",
                    background: "transparent",
                    color: "#9CA3AF",
                    cursor: currentPage === 1 ? "not-allowed" : "pointer",
                    opacity: currentPage === 1 ? 0.4 : 1,
                  }}
                  aria-label="Previous page"
                >
                  <FiChevronLeft size={16} />
                </button>

                {pageNumbers.map((page) => (
                  <button
                    key={page}
                    type="button"
                    onClick={() => handlePageChange(page)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      minWidth: 32,
                      height: 32,
                      borderRadius: 6,
                      padding: "0 10px",
                      border: "none",
                      fontSize: "14px",
                      fontWeight: 500,
                      cursor: "pointer",
                      backgroundColor:
                        currentPage === page ? FIGMA.navy : "#F0F4F8",
                      color: currentPage === page ? "#FFFFFF" : "#374151",
                    }}
                    aria-current={currentPage === page ? "page" : undefined}
                  >
                    {page}
                  </button>
                ))}

                <button
                  type="button"
                  onClick={() =>
                    handlePageChange(Math.min(totalPages, currentPage + 1))
                  }
                  disabled={currentPage === totalPages}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 32,
                    height: 32,
                    border: "none",
                    background: "transparent",
                    color: "#9CA3AF",
                    cursor: currentPage === totalPages ? "not-allowed" : "pointer",
                    opacity: currentPage === totalPages ? 0.4 : 1,
                  }}
                  aria-label="Next page"
                >
                  <FiChevronRight size={16} />
                </button>
              </nav>
            </div>
          </div>
        </>
      )}

      <Modal show={!!previewImage} onHide={() => setPreviewImage("")} centered>
        <Modal.Header closeButton>
          <Modal.Title>Image Preview</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <img
            src={previewImage}
            alt="Preview"
            style={{
              width: "100%",
              maxHeight: 400,
              objectFit: "contain",
              borderRadius: 5,
            }}
          />
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default ComplaintExpandableTable;
